import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Checkbox, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import debounce from 'lodash.debounce';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ListItem from '@mui/material/ListItem';

import { SearchIcon } from '@atoms/Icons';

const icon = <CheckBoxOutlineBlankIcon sx={{ width: '12px', height: '12px' }} />;
const checkedIcon = <CheckBoxIcon sx={{ width: '12px', height: '12px' }} />;

interface TableSearchProps {
  defaultSearch?: string;
  defaultSearchBy?: any;
  placeholder?: string;
  showFilterBySelect?: boolean;
  filterBySelectOptions?: { value: string; label: string }[];
  autocompleteClassname?: string;
  listBoxClassName?: string;
  popperClassName?: string;
  isMultipleSelect?: boolean;
  isMultipleSearch?: boolean;
  onSearch: (search: string, filterBy?: any) => void;
}

export const TableSearch: FC<TableSearchProps> = ({
  defaultSearch = '',
  defaultSearchBy,
  placeholder = 'Search for User name and Email',
  filterBySelectOptions = [],
  showFilterBySelect,
  autocompleteClassname,
  listBoxClassName,
  popperClassName,
  onSearch,
  isMultipleSelect = false,
  isMultipleSearch = false,
}) => {
  const defaultFilterBy = useMemo(() => {
    if (!defaultSearchBy || !defaultSearchBy.length) {
      return isMultipleSelect ? [] : null;
    }

    if (isMultipleSelect) {
      return filterBySelectOptions.filter((option) => defaultSearchBy.includes(option.value));
    }

    return filterBySelectOptions.find((option) => option.value === defaultSearchBy[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSearchBy, filterBySelectOptions]);

  const [search, setSearch] = useState(defaultSearch);
  const [filterBy, setFilterBy] = useState<any>(defaultFilterBy);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((_search: string, _filterBy?: any) => {
      if (isMultipleSelect) {
        onSearch(_search, _filterBy?.map((item: any) => item.value));
      } else {
        onSearch(_search, _filterBy?.value || '');
      }
    }, 500),
    [],
  );

  useEffect(() => {
    debouncedSearch(search, filterBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, debouncedSearch]);

  useEffect(() => {
    if (!search) {
      return;
    }

    debouncedSearch(search, filterBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBy, debouncedSearch]);

  const onChange = (newValue: any) => {
    if (isMultipleSelect) {
      setFilterBy(!newValue.length ? [] : newValue);
    } else {
      setFilterBy(!newValue ? '' : newValue);
    }
  };

  const onChangeAutoComplete = (_event: React.SyntheticEvent<Element, Event>, newValue: any) =>
    onChange(newValue);

  const onChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setSearch(e.target.value);

  return (
    <Box display="flex" alignItems="center" width="100%" border="1px solid #ccc" borderRadius="8px">
      {showFilterBySelect && (
        <Autocomplete
          multiple={isMultipleSelect}
          className={autocompleteClassname}
          options={filterBySelectOptions}
          value={filterBy}
          onChange={onChangeAutoComplete}
          disableClearable
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          ListboxProps={{
            className: listBoxClassName,
          }}
          renderOption={(props, option, { selected }) => (
            <ListItem
              {...props}
              sx={{
                fontSize: '12px',
                lineHeight: '20px',
                color: '#A3A7B0',
                padding: '10px',
              }}
            >
              <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
              {option.label}
            </ListItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                (isMultipleSelect && !!filterBy?.length) || (!isMultipleSelect && !!filterBy)
                  ? ''
                  : 'All'
              }
              variant="outlined"
              size="small"
              sx={{
                fontSize: '14px',
                border: 'none',
                '.MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            />
          )}
          sx={{
            minWidth: '150px',
            maxWidth: '300px',
            position: 'relative',
            fontSize: '14px',
            '&:after': {
              content: '""',
              position: 'absolute',
              top: '15%',
              right: '-8px',
              height: '70%',
              width: '0.5px',
              backgroundColor: '#ccc',
            },
            marginRight: '8px',
            '.MuiOutlinedInput-root': {
              '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
            },
            '.MuiAutocomplete-popupIndicator': {
              color: '#888',
            },
            ':after': {
              borderLeft: '1px solid #ccc',
            },
          }}
          openOnFocus
          componentsProps={{
            popper: {
              className: popperClassName,
            },
          }}
        />
      )}
      <Box position="relative" display="flex" alignItems="center" flex="1">
        <Typography component="span" sx={{ position: 'absolute', left: '12px', color: '#ccc' }}>
          <SearchIcon />
        </Typography>
        <TextField
          id="table-search-input"
          {...(isMultipleSearch ? { multiline: true, rows: 1 } : {})}
          value={search}
          onChange={onChangeSearchInput}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            paddingLeft: '36px',
            '.MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '.MuiOutlinedInput-root': {
              padding: 0,
              '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
            },
          }}
        />
        {search && (
          <IconButton
            sx={{ position: 'absolute', right: '12px', color: '#ccc' }}
            onClick={() => setSearch('')}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
